/**
 * The copyright caption service implementation. Provides a caption that is supposed to be display on link providing to
 * full copyright notice. The response is not changed often. This service is supposed to be called once when user have
 * the map displayed for the first time.
 *
 * The tomtom.copyrightCaption() method creates a new instance of service. Parameter can be passed on creation or
 * provided via convenient function that can be chained. The method go() performs the actual call.
 *
 * The call is asynchronous therefore user have two options to handle response, either by passing callback function or
 * by using [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise). The
 * SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @class copyrightCaption
 */
define('services/copyrightCaption',["rest/copyright", "model/copyright/copyrightCaption", "./chain", "./validators"],
    function (copyright, model, chain, validators) {
        "use strict";
        var fields = {
            /**
             * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
             * tomtom.key or tomtom.mapsKey function. But also per service instance on initialization or
             * with this method.
             *
             * The API Key can be issued from http://developer.tomtom.com
             *
             * @method key
             * @chainable
             * @param {String} key API Key valid for requested service.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {copyrightCaption|String} Method returns the service instance allowing chaining or key value if
             *     argument is omitted.
             */
            key: {validator: validators.string},
            /**
             * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] Protocol type, one of _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {copyrightCaption|String} Method returns the service instance allowing chaining or _protocol_
             * value if argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(["http", "https"], "protocol")},
            baseUrl: {validator: validators.string},
            serviceUrl: {validator: validators.string}
        };

        /**
         * The function performing actual call to the web service.
         *
         * Callback function takes a response parameter and fail callback takes error as only parameter.
         *
         * The final response from service is a string containing the caption i.e.:
         *     © 1992 - 2016 TomTom.
         *
         * This text have to be display as a copyright prompt when using TomTom services. The caption should be
         * clickable. User should be presented with full copyright notice when caption is clicked. For further
         * documentation please check caption class.
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise.
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.copyrightCaption.key(<Your API key>);
         * tomtom.copyrightCaption().go(myFunction);
         * ```
         */
        /**
         * A setter and getter for callback function. This function is called after receiving successful response from
         * service. It takes one argument which is the caption text.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} If the given argument is not a function.
         * @return {copyrightCaption|Function} Method returns the service instance allowing chaining or function if
         *     argument is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {copyrightCaption|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        function handleChain(data, callback) {
            copyright.caption(data, function (response) {
                callback.call(this, model(response));
            });
        }

        /**
         * The method to instantiates the service. The options can be passed on creation or later during the execution
         * using function with the name matching parameter name. Each option can be retrieved using the exactly same
         * function but without parameter. The instance of service can be reused by calling multiple times go()
         * function.
         *
         * @method copyrightCaption
         *
         * The copyright caption service implementation. Creates a new instance of service call. Parameters can be
         *     passed on creation or provided via convenient functions that can be chained. The method go() performs
         *     the actual call. The call is asynchronous therefore user needs to pass callback function.
         *
         *
         * @method copyrightCaption
         * @param {Object} [options] Options to be passed to the copyrightCaption call.
         * @param {String} [options.key] This is optional if key was already passed.
         * @param {Function} [options.callback] The callback function to be called on success.
         * @param {Function} [options.fail] The callback function to be called on failure. By default error is logged
         *     on console and thrown.
         * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
         * @return {copyrightCaption} Method returns new object that allows communication with the service.
         *
         * @example
         * ```javascript
         *
         * tomtom.copyrightCaption().go().then(function(response) {
         *   console.log(response);
         * });
         * ```
         */
        return function (options) {
            return chain(fields, handleChain, options);
        };
    });

